/**
 * Quiz Management Screen
 * List all quizzes and allow creating/managing live quiz sessions
 */

import React, { useState, useCallback, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  Alert,
  ActivityIndicator,
} from "react-native";
import { SafeAreaView, useSafeAreaInsets } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { useThemeColors, useTheme } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";

/**
 * Quiz type
 */
interface Quiz {
  id: string;
  name: string;
  description: string | null;
  icon: string;
  color: string;
  weekend_id: string | null;
  is_active: boolean;
  is_weekend_enabled: boolean;
  is_live_mode: boolean;
  time_per_question: number;
  points_per_correct: number;
  bonus_speed: number;
  created_at: string;
  questions_count?: number;
}

/**
 * Live session type
 */
interface LiveSession {
  id: string;
  quiz_id: string;
  status: string;
  current_question_index: number;
  total_questions: number;
  created_at: string;
}

export default function QuizListScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const { isDark } = useTheme();
  const { activeWeekend, roles } = useAuth();
  const insets = useSafeAreaInsets();

  // State
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [activeSessions, setActiveSessions] = useState<LiveSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [startingQuiz, setStartingQuiz] = useState<string | null>(null);

  /**
   * Fetch quizzes and active sessions
   */
  const fetchData = useCallback(async () => {
    try {
      // Fetch quizzes with question count
      const { data: quizData, error: quizError } = await supabase
        .from("quizzes")
        .select(`
          *,
          questions_count:quiz_questions(count)
        `)
        .order("created_at", { ascending: false });

      if (quizError) {
        console.error("Error fetching quizzes:", quizError);
      } else {
        // Transform the count
        const quizzesWithCount = (quizData || []).map((q: any) => ({
          ...q,
          questions_count: q.questions_count?.[0]?.count || 0,
        }));
        setQuizzes(quizzesWithCount);
      }

      // Fetch active live sessions
      const { data: sessionData, error: sessionError } = await supabase
        .from("quiz_live_sessions")
        .select("*")
        .in("status", ["lobby", "playing", "section_break", "showing_ranking"])
        .order("created_at", { ascending: false });

      if (!sessionError && sessionData) {
        setActiveSessions(sessionData);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
  }, [fetchData]);

  /**
   * Toggle weekend enabled for quiz
   */
  const handleToggleWeekend = async (quiz: Quiz) => {
    if (!activeWeekend?.id) {
      Alert.alert("Geen Weekend", "Er is geen actief weekend geselecteerd");
      return;
    }

    const { error } = await supabase
      .from("quizzes")
      .update({
        is_weekend_enabled: !quiz.is_weekend_enabled,
        weekend_id: !quiz.is_weekend_enabled ? activeWeekend.id : quiz.weekend_id,
        updated_at: new Date().toISOString(),
      })
      .eq("id", quiz.id);

    if (error) {
      Alert.alert("Fout", "Kon quiz niet bijwerken");
      return;
    }

    setQuizzes((prev) =>
      prev.map((q) =>
        q.id === quiz.id
          ? { ...q, is_weekend_enabled: !q.is_weekend_enabled }
          : q
      )
    );
  };

  /**
   * Start a live quiz session
   */
  const handleStartLiveQuiz = async (quiz: Quiz) => {
    if (!quiz.questions_count || quiz.questions_count < 1) {
      Alert.alert(
        "Geen Vragen",
        "Deze quiz heeft nog geen vragen. Voeg eerst vragen toe voordat je de quiz start."
      );
      return;
    }

    // Check if there's already an active session for this quiz
    const existingSession = activeSessions.find(s => s.quiz_id === quiz.id);
    if (existingSession) {
      // Navigate to the existing session
      router.push(`/(admin)/games/quiz/control?sessionId=${existingSession.id}` as any);
      return;
    }

    // Navigate to control screen which will create a new session
    router.push(`/(admin)/games/quiz/control?quizId=${quiz.id}` as any);
  };

  /**
   * Continue an existing live session
   */
  const handleContinueSession = (session: LiveSession) => {
    router.push(`/(admin)/games/quiz/control?sessionId=${session.id}` as any);
  };

  /**
   * Delete quiz
   */
  const handleDeleteQuiz = (quiz: Quiz) => {
    Alert.alert(
      "Quiz Verwijderen",
      `Weet je zeker dat je "${quiz.name}" wilt verwijderen?\n\nAlle vragen worden ook verwijderd.`,
      [
        { text: "Annuleren", style: "cancel" },
        {
          text: "Verwijderen",
          style: "destructive",
          onPress: async () => {
            const { error } = await supabase
              .from("quizzes")
              .delete()
              .eq("id", quiz.id);

            if (error) {
              Alert.alert("Fout", "Kon quiz niet verwijderen");
              return;
            }

            setQuizzes((prev) => prev.filter((q) => q.id !== quiz.id));
          },
        },
      ]
    );
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * Get active session for a quiz
   */
  const getActiveSession = (quizId: string) => {
    return activeSessions.find(s => s.quiz_id === quizId);
  };

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }} edges={["top", "bottom"]}>
      {/* Header */}
      <View className="px-4 py-4 flex-row items-center justify-between">
        <View className="flex-row items-center">
          <TouchableOpacity
            onPress={() => router.back()}
            className="w-10 h-10 rounded-full items-center justify-center mr-3"
            style={{ backgroundColor: colors.surface }}
            accessibilityRole="button"
            accessibilityLabel="Terug"
          >
            <Ionicons name="arrow-back" size={22} color={colors.text} />
          </TouchableOpacity>
          <View>
            <Text className="text-xl font-bold" style={{ color: colors.text }}>
              Quizzen
            </Text>
            <Text className="text-sm" style={{ color: colors.textSecondary }}>
              {quizzes.length} quiz{quizzes.length !== 1 ? "zen" : ""} • Live modus
            </Text>
          </View>
        </View>

        <TouchableOpacity
          onPress={() => router.push("/(admin)/games/quiz/create-quiz")}
          className="w-10 h-10 rounded-full items-center justify-center"
          style={{ backgroundColor: colors.primary }}
          accessibilityRole="button"
          accessibilityLabel="Nieuwe quiz"
        >
          <Ionicons name="add" size={24} color="#FFFFFF" />
        </TouchableOpacity>
      </View>

      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
          />
        }
      >
        {isLoading ? (
          <View className="py-12 items-center">
            <ActivityIndicator size="large" color={colors.primary} />
          </View>
        ) : quizzes.length === 0 ? (
          <View className="py-12 items-center px-4">
            <View
              className="w-20 h-20 rounded-full items-center justify-center mb-4"
              style={{ backgroundColor: `${colors.primary}20` }}
            >
              <Ionicons name="help-circle" size={40} color={colors.primary} />
            </View>
            <Text className="text-lg font-semibold mb-2" style={{ color: colors.text }}>
              Geen quizzen
            </Text>
            <Text className="text-center mb-6" style={{ color: colors.textSecondary }}>
              Maak je eerste quiz aan om live te spelen tijdens het familieweekend
            </Text>
            <TouchableOpacity
              onPress={() => router.push("/(admin)/games/quiz/create-quiz")}
              className="px-6 py-3 rounded-xl flex-row items-center"
              style={{ backgroundColor: colors.primary }}
            >
              <Ionicons name="add" size={20} color="#FFFFFF" />
              <Text className="ml-2 font-bold text-white">Nieuwe Quiz</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View className="px-4 pb-4">
            {/* Live Quiz Info */}
            <View
              className="rounded-xl p-4 mb-4 flex-row items-start"
              style={{
                backgroundColor: isDark ? "#1E3A5F" : "#EFF6FF",
                borderWidth: 1,
                borderColor: isDark ? "#3B82F6" : "#BFDBFE",
              }}
            >
              <Ionicons name="tv-outline" size={22} color="#3B82F6" />
              <View className="ml-3 flex-1">
                <Text className="font-semibold" style={{ color: isDark ? "#93C5FD" : "#1E40AF" }}>
                  Kahoot-stijl Live Quizzen
                </Text>
                <Text className="text-sm mt-1" style={{ color: isDark ? "#60A5FA" : "#3B82F6" }}>
                  Start een quiz en controleer deze vanaf je telefoon. Vragen worden op een groot scherm getoond.
                </Text>
              </View>
            </View>

            {/* Active Sessions Banner */}
            {activeSessions.length > 0 && (
              <View className="mb-4">
                <Text className="font-semibold mb-2" style={{ color: colors.text }}>
                  Actieve Sessies
                </Text>
                {activeSessions.map((session) => {
                  const quiz = quizzes.find(q => q.id === session.quiz_id);
                  if (!quiz) return null;
                  
                  return (
                    <TouchableOpacity
                      key={session.id}
                      onPress={() => handleContinueSession(session)}
                      className="rounded-xl p-4 mb-2 flex-row items-center"
                      style={{
                        backgroundColor: "#10B98120",
                        borderWidth: 2,
                        borderColor: "#10B981",
                      }}
                    >
                      <View
                        className="w-12 h-12 rounded-xl items-center justify-center mr-3"
                        style={{ backgroundColor: `${quiz.color}25` }}
                      >
                        <Ionicons name={quiz.icon as any} size={24} color={quiz.color} />
                      </View>
                      <View className="flex-1">
                        <Text className="font-bold" style={{ color: colors.text }}>
                          {quiz.name}
                        </Text>
                        <View className="flex-row items-center mt-1">
                          <View
                            className="w-2 h-2 rounded-full mr-2"
                            style={{ backgroundColor: "#10B981" }}
                          />
                          <Text className="text-sm" style={{ color: "#10B981" }}>
                            {session.status === "lobby" ? "Lobby - wachten op spelers" :
                             session.status === "playing" ? `Vraag ${session.current_question_index + 1}/${session.total_questions}` :
                             session.status === "section_break" ? "Sectie pauze" :
                             "Ranking tonen"}
                          </Text>
                        </View>
                      </View>
                      <View
                        className="px-3 py-2 rounded-lg"
                        style={{ backgroundColor: "#10B981" }}
                      >
                        <Text className="text-white font-semibold text-sm">Hervat</Text>
                      </View>
                    </TouchableOpacity>
                  );
                })}
              </View>
            )}

            {/* Quiz List */}
            <Text className="font-semibold mb-2" style={{ color: colors.text }}>
              Alle Quizzen
            </Text>
            
            {quizzes.map((quiz) => {
              const activeSession = getActiveSession(quiz.id);
              
              return (
                <QuizCard
                  key={quiz.id}
                  quiz={quiz}
                  colors={colors}
                  hasActiveSession={!!activeSession}
                  onPress={() => router.push(`/(admin)/games/quiz/${quiz.id}/questions`)}
                  onEdit={() => router.push(`/(admin)/games/quiz/${quiz.id}`)}
                  onToggleWeekend={() => handleToggleWeekend(quiz)}
                  onDelete={() => handleDeleteQuiz(quiz)}
                  onStartLive={() => handleStartLiveQuiz(quiz)}
                  isStarting={startingQuiz === quiz.id}
                />
              );
            })}
          </View>
        )}

        <View className="h-24" />
      </ScrollView>

      {/* Floating Add Button */}
      {quizzes.length > 0 && (
        <TouchableOpacity
          onPress={() => router.push("/(admin)/games/quiz/create-quiz")}
          className="absolute right-6 w-14 h-14 rounded-full items-center justify-center shadow-lg"
          style={{
            bottom: Math.max(insets.bottom, 24) + 8,
            backgroundColor: colors.primary,
            shadowColor: colors.primary,
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.3,
            shadowRadius: 8,
            elevation: 8,
          }}
          accessibilityRole="button"
          accessibilityLabel="Nieuwe quiz toevoegen"
        >
          <Ionicons name="add" size={28} color="#FFFFFF" />
        </TouchableOpacity>
      )}
    </SafeAreaView>
  );
}

/**
 * Quiz Card Component
 */
interface QuizCardProps {
  quiz: Quiz;
  colors: ReturnType<typeof useThemeColors>;
  hasActiveSession: boolean;
  onPress: () => void;
  onEdit: () => void;
  onToggleWeekend: () => void;
  onDelete: () => void;
  onStartLive: () => void;
  isStarting: boolean;
}

function QuizCard({
  quiz,
  colors,
  hasActiveSession,
  onPress,
  onEdit,
  onToggleWeekend,
  onDelete,
  onStartLive,
  isStarting,
}: QuizCardProps) {
  const maxPoints = (quiz.questions_count || 0) * (quiz.points_per_correct + quiz.bonus_speed);
  const hasQuestions = (quiz.questions_count || 0) > 0;

  return (
    <View
      className="rounded-xl mb-3 overflow-hidden"
      style={{
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
        opacity: quiz.is_active ? 1 : 0.6,
      }}
    >
      {/* Main Card Content */}
      <TouchableOpacity
        onPress={onPress}
        activeOpacity={0.7}
        className="p-4"
      >
        <View className="flex-row items-start">
          {/* Quiz Icon */}
          <View
            className="w-14 h-14 rounded-xl items-center justify-center mr-3"
            style={{ backgroundColor: `${quiz.color}20` }}
          >
            <Ionicons
              name={quiz.icon as any}
              size={28}
              color={quiz.color}
            />
          </View>

          {/* Quiz Info */}
          <View className="flex-1">
            <Text className="font-bold text-base" style={{ color: colors.text }}>
              {quiz.name}
            </Text>
            {quiz.description && (
              <Text
                className="text-sm mt-0.5"
                style={{ color: colors.textSecondary }}
                numberOfLines={1}
              >
                {quiz.description}
              </Text>
            )}

            {/* Stats Row */}
            <View className="flex-row items-center mt-2 flex-wrap">
              <View
                className="flex-row items-center px-2 py-1 rounded mr-2 mb-1"
                style={{ backgroundColor: hasQuestions ? colors.surfaceSecondary : "#EF444420" }}
              >
                <Ionicons 
                  name="help-circle" 
                  size={12} 
                  color={hasQuestions ? colors.textSecondary : "#EF4444"} 
                />
                <Text 
                  className="text-xs ml-1" 
                  style={{ color: hasQuestions ? colors.textSecondary : "#EF4444" }}
                >
                  {quiz.questions_count || 0} vragen
                </Text>
              </View>

              {hasQuestions && (
                <View
                  className="flex-row items-center px-2 py-1 rounded mr-2 mb-1"
                  style={{ backgroundColor: "#F59E0B20" }}
                >
                  <Ionicons name="trophy" size={12} color="#F59E0B" />
                  <Text className="text-xs ml-1" style={{ color: "#F59E0B" }}>
                    max {maxPoints} pts
                  </Text>
                </View>
              )}

              <View
                className="flex-row items-center px-2 py-1 rounded mb-1"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Ionicons name="time" size={12} color={colors.textSecondary} />
                <Text className="text-xs ml-1" style={{ color: colors.textSecondary }}>
                  {quiz.time_per_question}s
                </Text>
              </View>
            </View>
          </View>

          {/* Quick Actions */}
          <View className="flex-row items-center">
            {/* Edit */}
            <TouchableOpacity
              onPress={onEdit}
              className="w-8 h-8 rounded-full items-center justify-center mr-1"
              style={{ backgroundColor: colors.surfaceSecondary }}
              accessibilityLabel="Bewerk quiz"
            >
              <Ionicons name="pencil" size={14} color={colors.textSecondary} />
            </TouchableOpacity>

            {/* Delete */}
            <TouchableOpacity
              onPress={onDelete}
              className="w-8 h-8 rounded-full items-center justify-center"
              style={{ backgroundColor: colors.error + "20" }}
              accessibilityLabel="Verwijder quiz"
            >
              <Ionicons name="trash" size={14} color={colors.error} />
            </TouchableOpacity>
          </View>
        </View>
      </TouchableOpacity>

      {/* Start Live Quiz Button */}
      <View 
        className="px-4 pb-4"
        style={{ borderTopWidth: 1, borderTopColor: colors.border }}
      >
        <View className="pt-3 flex-row items-center">
          <TouchableOpacity
            onPress={onStartLive}
            disabled={isStarting || !hasQuestions}
            className="flex-1 flex-row items-center justify-center py-3 rounded-xl mr-2"
            style={{ 
              backgroundColor: hasActiveSession ? "#10B981" : 
                              hasQuestions ? quiz.color : colors.border,
              opacity: !hasQuestions ? 0.5 : 1,
            }}
            accessibilityRole="button"
            accessibilityLabel={hasActiveSession ? "Hervat live quiz" : "Start live quiz"}
          >
            {isStarting ? (
              <ActivityIndicator color="#FFFFFF" size="small" />
            ) : (
              <>
                <Ionicons 
                  name={hasActiveSession ? "play" : "tv-outline"} 
                  size={18} 
                  color="#FFFFFF" 
                />
                <Text className="ml-2 font-bold text-white">
                  {hasActiveSession ? "Hervat Live Quiz" : 
                   hasQuestions ? "Start Live Quiz" : "Voeg eerst vragen toe"}
                </Text>
              </>
            )}
          </TouchableOpacity>

          {/* Weekend Toggle */}
          <TouchableOpacity
            onPress={onToggleWeekend}
            className="w-12 h-12 rounded-xl items-center justify-center"
            style={{
              backgroundColor: quiz.is_weekend_enabled ? "#10B98120" : colors.surfaceSecondary,
              borderWidth: quiz.is_weekend_enabled ? 2 : 0,
              borderColor: "#10B981",
            }}
            accessibilityRole="switch"
            accessibilityState={{ checked: quiz.is_weekend_enabled }}
            accessibilityLabel="Activeer voor weekend"
          >
            <Ionicons
              name={quiz.is_weekend_enabled ? "trophy" : "trophy-outline"}
              size={20}
              color={quiz.is_weekend_enabled ? "#10B981" : colors.textTertiary}
            />
          </TouchableOpacity>
        </View>

        {/* Weekend status text */}
        {quiz.is_weekend_enabled && (
          <View className="flex-row items-center mt-2">
            <Ionicons name="checkmark-circle" size={14} color="#10B981" />
            <Text className="text-xs ml-1" style={{ color: "#10B981" }}>
              Geactiveerd voor weekend competitie
            </Text>
          </View>
        )}
      </View>
    </View>
  );
}
